'use client';
import {useEffect, useState, useRef, useCallback} from 'react';
import MainLiveApi from '@/api/main/main-live';
import {getTimeStamp, isLogin} from '@/utils/main';
import {LIVE_INIT_LIST, MAIN_LIST_CNT} from '@/constants/main';
import {LiveGroupList} from '@/components/main/live/LiveGroupList';
import {LiveGroupBothBanner} from '@/components/main/live/group/LiveGroupBothBanner';
import {Banner} from '@/components/common';
import {useHiddenStreamer} from '../../../common/hooks/useHiddenStreamer';
import useGlobalUIStore from '@/stores';

const AWARD_MAP = {
  award2024: {
    categoryNo: '00950000',
    categoryName: '2024 스트리머 대상',
  },
  award2025: {
    categoryNo: process.env.BUILD_ENV === 'develop' ? '00950000' : '01030000',
    categoryName: '2025 SOOP 스트리머 대상',
  },
};

export const LiveGroupAward = ({liveType}) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('view_cnt');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState({
    active: liveType,
    categoryNo: AWARD_MAP[liveType].categoryNo,
    categoryName: AWARD_MAP[liveType].categoryName,
  });
  const [hiddenStreamer, setHiddenStreamer] = useState([]);
  const [mainListInfo, setMainListInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [broadList, setBroadList] = useState(LIVE_INIT_LIST);
  const {
    remainCount,
    slicedBroadList,
    setRemainCount,
    filterBroadList,
    setSlicedBroadList,
    handleOnClickHiddenStreamer,
  } = useHiddenStreamer('LIVE', selectedMenu);
  const {refreshTimestamp} = useGlobalUIStore();
  const categoryList = useRef([
    {
      broadCateNo: AWARD_MAP[liveType].categoryNo,
    },
  ]);

  /**
   * 카테고리 이름과 번호를 사용해 해당 카테고리 아이템의 ref를 찾는다.
   * @param {*} cateName 카테고리 이름
   * @param {*} cateNo 카테고리 번호
   * @returns
   */
  const findCategory = useCallback(
    (cateName, cateNo = null) => {
      if (!cateNo) {
        // 카테고리 번호 미사용 시
        return categoryList.current?.find(
          (category) => category.cateName === cateName,
        );
      } else {
        // 카테고리 번호 사용 시
        return categoryList.current?.find(
          (category) =>
            category.cateName === cateName && category.broadCateNo === cateNo,
        );
      }
    },
    [categoryList],
  );

  const handleOnClickMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handleOnClickSort = (type) => {
    if (type === order) {
      return;
    }
    handleInitState(type);
  };

  const handleInitState = (order = 'view_cnt') => {
    setPage(1);
    setOrder(order);
    setRemainCount(0);
    setSlicedBroadList([]);
    setBroadList(LIVE_INIT_LIST);
  };

  /**
   * 선택한 카테고리 아이템의 상태를 통해 해당 카테고리에 포함되는 방송 목록을 불러온다.
   * @param {string} selectedCategoryName 선택한 카테고리 이름
   * @param {string} selectedCategoryNo 선택한 카테고리 번호
   * @returns
   */
  const loadBroadList = async (selectedCategoryName, selectedCategoryNo) => {
    try {
      if (!selectedCategoryNo) {
        // 카테고리 번호가 주어지지 않을 경우 X
        return;
      }

      /**
       * 선택한 카테고리 조건에 부합하는 카테고리 아이템 ref 객체
       * 선택한 조건에 부합하는 아이템이 없으면 null
       * API에는 selectedCategoryNo 기반으로 질의
       */
      const selectedCategoryItem = findCategory(
        selectedCategoryName,
        selectedCategoryNo,
      );

      let queryParams = {
        selectType: 'cate',
        selectValue: selectedCategoryNo, // API에는 카테고리 번호를 사용
        orderType: order,
        pageNo: page,
        lang: 'ko_KR',
        _: getTimeStamp(),
      };

      // 요청 파라미터의 szActionType이 3일땐 연령제한 방송만 가져온다.
      queryParams.szActionType =
        !!selectedCategoryItem && selectedCategoryItem.isAdult ? 3 : 2;

      const response = await MainLiveApi.getBroadList(queryParams);
      if (response && response.broad) {
        const {totalCnt, broad} = response;
        if (Number(totalCnt) > 0 && broad.length > 0) {
          const totalPage = Math.ceil(Number(totalCnt) / MAIN_LIST_CNT);
          const isMore = Number(page) < Number(totalPage);
          if (hiddenStreamer.length > 0) {
            filterBroadList(
              'live',
              page,
              broad,
              isMore,
              hiddenStreamer,
              setBroadList,
            );
          } else {
            setBroadList({
              isMore: isMore,
              list: [...broadList.list, ...broad],
            });
          }
        }
      }
    } catch (error) {
      setBroadList(LIVE_INIT_LIST);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 숨김 처리된 스트리머 목록을 가져와 방송 목록에 반영한다.
   */
  const getHiddenStreamerList = async () => {
    try {
      const response = await MainLiveApi.getHiddenStreamerList();
      if (response && response.result === 1) {
        if (response.data.length === 0) {
          loadBroadList(
            selectedCategory.categoryName,
            selectedCategory.categoryNo,
          );
        } else {
          setHiddenStreamer([...response.data]);
        }
      }
    } catch (error) {
      loadBroadList(selectedCategory.categoryName, selectedCategory.categoryNo);
    }
  };

  /**
   * 정적 핫이슈, 배너정보 등을 가져온다.
   */
  const getStaticBroadListMainInfo = async () => {
    try {
      const response = await MainLiveApi.getStaticBroadListMainInfo();
      if (response) {
        setMainListInfo(response);
      }
    } catch (error) {
      setMainListInfo({});
    }
  };

  useEffect(() => {
    // 방송 목록 영역 -> 더 보기 클릭 시 반응
    if (page === 1) {
      setIsLoading(true);
    }
    if (broadList.isMore && selectedCategory.active !== '') {
      if (isLogin()) {
        getHiddenStreamerList();
      } else {
        loadBroadList(
          selectedCategory.categoryName,
          selectedCategory.categoryNo,
        );
      }
    } else {
      if (slicedBroadList.length > 0) {
        setBroadList({
          ...broadList,
          list: [...broadList.list, ...slicedBroadList],
        });
        setSlicedBroadList([]);
      } else {
        if (
          page === 1 &&
          remainCount === 0 &&
          selectedCategory.categoryNo.length > 0
        ) {
          if (isLogin()) {
            getHiddenStreamerList();
          } else {
            loadBroadList(
              selectedCategory.categoryName,
              selectedCategory.categoryNo,
            );
          }
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [page, order, selectedCategory, refreshTimestamp]);

  useEffect(() => {
    if (liveType === selectedMenu) {
      handleInitState();
    }
  }, [selectedMenu]);

  useEffect(() => {
    if (remainCount > 0) {
      setPage((prev) => prev + 1);
    }
  }, [remainCount]);

  useEffect(() => {
    if (hiddenStreamer.length > 0) {
      loadBroadList(selectedCategory.categoryName, selectedCategory.categoryNo);
    }
  }, [hiddenStreamer]);

  useEffect(() => {
    if (refreshTimestamp) {
      handleInitState();
    }
    window.scrollTo(0, 0);
  }, [refreshTimestamp]);

  useEffect(() => {
    setSelectedMenu(liveType);
  }, [liveType]);

  useEffect(() => {
    getStaticBroadListMainInfo();
  }, []);

  return (
    <>
      <div id='container' className='_live'>
        <Banner
          type={'sub'}
          category={selectedMenu}
          posterCategory={selectedCategory}
        />
        <LiveGroupBothBanner
          selectedMenu={selectedMenu}
          mainListInfo={mainListInfo}
          selectedCategory={selectedCategory}
        />
        <LiveGroupList
          order={order}
          broadList={broadList}
          isLoading={isLoading}
          selectedMenu={selectedMenu}
          selectedTitle={selectedCategory.categoryName}
          slicedBroadList={slicedBroadList}
          location={selectedMenu}
          handleOnClickMore={handleOnClickMore}
          handleOnClickSort={handleOnClickSort}
          handleOnClickHiddenStreamer={handleOnClickHiddenStreamer}
        />
      </div>
    </>
  );
};
